import React, { useState, useEffect } from "react";
import { Button, message, Spin } from "antd";
import { DownloadOutlined, FileOutlined } from "@ant-design/icons";
import { Popup } from "../../atoms/Popup";
import moment from "moment";
import { useKeycloak } from '@react-keycloak/web';

/**
 * Reusable Export Feature Component
 * 
 * @param {Object} props
 * @param {Function} props.onExport - Smart export handler. Backend decides direct vs S3. Should return { queued: true } when S3 export is queued.
 * @param {Function} props.getExportList - Function to fetch list of exported files - should return promise
 * @param {Function} props.getDownloadUrl - Function to get download URL for a file - should return promise
 * @param {Object} props.rowData - Data object containing total_records or filtered_records
 * @param {boolean} props.exportLoading - Loading state for Export button
 * @param {string} props.modalTitle - Title for the Files Exported modal (default: "Exported Files")
 * @param {string} props.exportType - Type identifier for the export (e.g., 'vpn_users', 'connected_users')
 * @param {Object} props.buttonStyles - Custom styles for buttons { export, exportAll, files }
 */
const ExportFeature = ({
  onExport,
  getExportList,
  getDownloadUrl,
  rowData,
  effectiveTotalRecords,
  exportLoading = false,
  modalTitle = "Exported Files",
  exportType = 'default',
  buttonStyles = {}
}) => {
  const { keycloak } = useKeycloak();
  
  // Backend decides direct vs S3. Frontend never fetches/decides thresholds.
  const exportAllThresholdRaw =
    process.env.REACT_APP_EXPORT_ALL_THRESHOLD || process.env.EXPORT_ALL_THRESHOLD || '';
  const exportAllThreshold = Number.parseInt(String(exportAllThresholdRaw || ''), 10);
  const isThresholdEnabled = Number.isFinite(exportAllThreshold) && exportAllThreshold > 0;
  
  // Get total records count (support multiple field names for different modules)
  const totalRecordsRaw = rowData?.total_records || rowData?.filtered_records || rowData?.total_users || 0;
  // If caller provides the "effective" count (e.g. filtered_records when filters are applied), prefer it.
  const totalRecords = typeof effectiveTotalRecords === 'number' ? effectiveTotalRecords : totalRecordsRaw;
  
  // Check if rowData is loaded (has data property or records count)
  const isDataLoaded = rowData && (
    rowData.data !== undefined || 
    rowData.connectedData !== undefined ||
    rowData.total_records !== undefined || 
    rowData.filtered_records !== undefined ||
    rowData.total_users !== undefined
  );
  
  const withinThreshold = !isThresholdEnabled || totalRecords <= exportAllThreshold;
  const showExportFeatures = isDataLoaded && totalRecords > 0 && withinThreshold;
  
  // State management
  const [downloadsPopUpVisible, setDownloadsPopUpVisible] = useState(false);
  const [downloadFiles, setDownloadFiles] = useState([]);
  const [downloadsLoading, setDownloadsLoading] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [hasExportFiles, setHasExportFiles] = useState(null); // null=unknown, true/false=known

  const fetchExportFiles = async () => {
    if (!getExportList) return { ok: false, files: [] };
    try {
      const res = await getExportList();
      const files = res?.data?.body?.files || res?.data?.body || [];
      const mappedFiles = files.map((f) => {
        if (typeof f === 'object' && f.filename) return f;
        return { filename: f, created_at: null, status: null };
      });
      const allowedStatuses = new Set(['pending', 'completed', 'failed']);
      const filteredFiles = mappedFiles.filter((file) => {
        const status = typeof file?.status === 'string' ? file.status.toLowerCase() : '';
        return allowedStatuses.has(status);
      });
      return { ok: true, files: filteredFiles };
    } catch (err) {
      console.error('Error fetching export list:', err);
      return { ok: false, files: [] };
    }
  };

  // Show "Files Exported" only if there is at least 1 export entry for this module (VPN vs Connected).
  // We pre-check once data is loaded to avoid showing the button when list is empty.
  useEffect(() => {
    let mounted = true;
    if (!isDataLoaded || !getExportList) return;
    (async () => {
      const { ok, files } = await fetchExportFiles();
      if (!mounted) return;
      if (ok) {
        setHasExportFiles(files.length > 0);
        setDownloadFiles(files);
      } else {
        // Unknown on error; keep null so we don't incorrectly hide a potentially non-empty list.
        setHasExportFiles(null);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataLoaded, exportType, getExportList]);

  // Default button styles
  const defaultExportBtnStyle = {
    minWidth: 110,
    fontWeight: 600,
    background: '#7c3aed',
    borderColor: '#7c3aed',
    borderRadius: 8,
    fontSize: 15,
    boxShadow: '0 2px 8px rgba(124, 60, 237, 0.08)',
  };

  const defaultExportAllBtnStyle = {
    minWidth: 120,
    fontWeight: 600,
    border: '1.5px solid #7c3aed',
    color: '#7c3aed',
    background: '#f8fafc',
    borderRadius: 8,
    fontSize: 15,
    boxShadow: '0 2px 8px rgba(124, 60, 237, 0.08)',
  };

  const defaultFilesBtnStyle = {
    minWidth: 140,
    fontWeight: 600,
    border: '1.5px solid #e5e7eb',
    color: '#fff',
    background: 'rgb(41 155 144)',
    borderRadius: 8,
    fontSize: 15,
  };

  const exportBtnStyle = { ...defaultExportBtnStyle, ...buttonStyles.export };
  const exportAllBtnStyle = { ...defaultExportAllBtnStyle, ...buttonStyles.exportAll };
  const filesBtnStyle = { ...defaultFilesBtnStyle, ...buttonStyles.files };

  // Fetch export files when popup opens
  useEffect(() => {
    if (downloadsPopUpVisible && getExportList) {
      setDownloadsLoading(true);
      fetchExportFiles().then(({ ok, files }) => {
        if (ok) {
          setDownloadFiles(files);
          setHasExportFiles(files.length > 0);
        } else {
          setDownloadFiles([]);
          message.error('Failed to fetch exported files');
        }
        setDownloadsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadsPopUpVisible]);

  // Single Export button behavior:
  // - Always call backend "smart export"
  // - Backend returns either a CSV stream (direct) or JSON {mode:'queued'} (S3)
  const handleSingleExport = async () => {
    if (!showExportFeatures) {
      message.warning('No data to export');
      return;
    }

    if (!onExport) {
      message.warning('Export functionality not configured');
      return;
    }

    const refreshHasExportFiles = async () => {
      const { ok, files } = await fetchExportFiles();
      if (ok) {
        setDownloadFiles(files);
        setHasExportFiles(files.length > 0);
        return files.length > 0;
      }
      return false;
    };

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    // Export handler may return { queued: true } for S3 exports.
    const result = await onExport();

    // Always refresh once after export, so the button can appear without page refresh.
    let found = await refreshHasExportFiles();

    // For queued exports, the DB record may appear a moment later (consumer creates it).
    // Poll a few times to make UI responsive without requiring refresh.
    if (result?.queued && !found) {
      for (let i = 0; i < 5; i++) {
        await sleep(1500);
        found = await refreshHasExportFiles();
        if (found) break;
      }
    }
  };

  // Parse requested date from filename or file object
  const parseRequestedDate = (file) => {
    try {
      // If file has created_at or requested_date property, use it directly
      if (file.created_at) {
        return moment(file.created_at).format('MMM DD, YYYY HH:mm:ss');
      }
      if (file.requested_date) {
        return moment(file.requested_date).format('MMM DD, YYYY HH:mm:ss');
      }
      
      // Fallback to parsing filename for backward compatibility
      const filename = typeof file === 'string' ? file : file.filename;
      const base = filename.split('/').pop();
      // Try to match common export filename patterns
      const match = base.match(/(\d{4}-\d{2}-\d{2})_(\d{2}-\d{2}-\d{2})/);
      if (!match) return '-';
      const [_, datePart, timePart] = match; // eslint-disable-line no-unused-vars
      const time = timePart.replace(/-/g, ':');
      return moment(`${datePart} ${time}`, 'YYYY-MM-DD HH:mm:ss').format('MMM DD, YYYY HH:mm:ss');
    } catch (e) {
      return '-';
    }
  };

  // Handle file download
  const handleDownload = async (file) => {
    if (!getDownloadUrl) {
      message.warning('Download functionality not configured');
      return;
    }

    setDownloadingFile(file.filename);
    try {
      const resellerId = keycloak?.tokenParsed?.reseller_id;
      const res = await getDownloadUrl(resellerId, file.filename);
      const url = res?.data?.body?.downloadUrl || res?.data?.downloadUrl;
      if (url) {
        window.open(url, '_blank');
      } else {
        message.error('Download URL not found');
      }
    } catch (e) {
      console.error('Download error:', e);
      message.error('Failed to get download URL');
    } finally {
      setDownloadingFile(null);
    }
  };

  // Popup styles
  const popupTableStyle = {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    overflow: 'hidden',
  };

  const popupThTd = {
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: 15,
  };

  const popupHeader = {
    marginBottom: 20,
    fontWeight: 700,
    fontSize: 18,
    color: '#7c3aed',
  };

  // Downloads popup content
  const downloadsPopupContent = {
    closable: true,
    visibility: downloadsPopUpVisible,
    class: "popup-modal",
    width: 1200,
    content: (
      <div style={{ padding: 24, borderRadius: 16, background: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
        <div style={{ ...popupHeader, fontSize: 22, marginBottom: 28 }}>{modalTitle}</div>
        {downloadsLoading ? (
          <div style={{ textAlign: 'center', padding: 48, fontSize: 18 }}><Spin /></div>
        ) : (
          <table style={{ ...popupTableStyle, fontSize: 16 }}>
            <thead>
              <tr>
                <th style={{ ...popupThTd, background: '#f8fafc', color: '#7c3aed', fontWeight: 700, fontSize: 16, width: 200 }}>Filename</th>
                <th style={{ ...popupThTd, background: '#f8fafc', color: '#7c3aed', fontWeight: 700, fontSize: 16, width: 200 }}>Requested Date</th>
                <th style={{ ...popupThTd, background: '#f8fafc', color: '#7c3aed', fontWeight: 700, fontSize: 16, width: 120 }}>Status</th>
                <th style={{ ...popupThTd, background: '#f8fafc', color: '#7c3aed', fontWeight: 700, fontSize: 16, width: 180 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {downloadFiles.length === 0 ? (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: 32 }}>No files found</td></tr>
              ) : downloadFiles.map((file, idx) => (
                <tr key={file.filename || idx}>
                  <td style={popupThTd}>{file.filename}</td>
                  <td style={popupThTd}>{parseRequestedDate(file)}</td>
                  <td style={popupThTd}>
                    <span style={{ 
                      padding: '4px 8px', 
                      borderRadius: '4px', 
                      fontSize: '12px',
                      fontWeight: '600',
                      background: file.status === 'completed' ? '#d4edda' : file.status === 'pending' ? '#fff3cd' : file.status === 'failed' ? '#f8d7da' : '#f8d7da',
                      color: file.status === 'completed' ? '#155724' : file.status === 'pending' ? '#856404' : file.status === 'failed' ? '#721c24' : '#721c24'
                    }}>
                      {file.status === 'failed' ? 'Failed' : file.status || 'cancelled'}
                    </span>
                  </td>
                  <td style={popupThTd}>
                    {file.status === 'completed' ? (
                      <Button
                        icon={<DownloadOutlined />}
                        type="primary"
                        style={{ borderRadius: 6, fontWeight: 600, background: '#7c3aed', borderColor: '#7c3aed', minWidth: 120 }}
                        loading={downloadingFile === file.filename}
                        onClick={() => handleDownload(file)}
                      >
                        Download
                      </Button>
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: '14px' }}>
                        {file.status === 'pending' ? 'Processing...' : file.status === 'failed' ? 'Export Failed' : 'Not Available'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    ),
    onCancel: () => setDownloadsPopUpVisible(false),
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 10 }}>
        {showExportFeatures && (
          <Button
            icon={<DownloadOutlined />}
            type="primary"
            size="large"
            onClick={handleSingleExport}
            loading={exportLoading}
            style={exportBtnStyle}
          >
            Export
          </Button>
        )}
        {getExportList && hasExportFiles === true && (
          <Button
            icon={<FileOutlined />}
            type="default"
            size="large"
            onClick={() => setDownloadsPopUpVisible(true)}
            loading={downloadsLoading}
            style={filesBtnStyle}
          >
            Files Exported
          </Button>
        )}
      </div>
      {getExportList && <Popup {...downloadsPopupContent} />}
    </>
  );
};

export default ExportFeature;

